import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { RiderType, DocumentUpload, Spoke } from "./types";

// Every image/video field stores the backend-returned URL (from
// uploadOnboardingImage), not the local device URI. Upload-on-pick.
export interface OnboardingData {
  riderType: RiderType;
  phoneNumber: string;
  isCpConversion: boolean;

  // Personal details (Figma 36/18)
  fullName: string;
  dateOfBirth: string;
  city: string;
  homePhoneNumber: string;
  refereeName: string;
  refereeNumber: string;

  // Self KYC — Aadhaar + PAN + DL + selfie
  aadhaarNumber: string;
  aadhaarPhotos: DocumentUpload; // { front, back } — URLs
  panNumber: string;
  panPhotos: DocumentUpload; // { front, back } — URLs (replaces single panPhoto)
  drivingLicenseNumber: string;
  drivingLicensePhotos: DocumentUpload; // { front, back } — URLs
  selfieUrl: string | null; // URL (replaces selfieUri)

  // Family-KYC (documentOwnership === 'family')
  documentOwnership: "self" | "family" | null;
  personalDocType: "AADHAAR" | "PAN" | "DL" | null;
  personalDocNumber: string;
  personalDocPhotos: DocumentUpload; // URLs

  // Laggan-only
  lagganVideoUrl: string | null;
  lagganFcName: string;
  addressPlot: string;
  addressArea: string;
  addressPincode: string;
  addressState: string;
  addressCity: string;

  // Spoke selection
  selectedSpoke: Spoke | null;

  // ─── Legacy fields kept for screen compat; new code should use the
  //     structured fields above. Remove once all screens migrate.
  panPhoto: string | null;
  selfieUri: string | null;
  currentAddress: string;
  refereePhone1: string;
  refereePhone2: string;
  lagganVideoUri: string | null;
  submittedByFCName: string;
  personalAadhaarNumber: string;
  personalAadhaarPhotos: DocumentUpload;
}

const INITIAL_DATA: OnboardingData = {
  riderType: "normal",
  phoneNumber: "",
  isCpConversion: false,

  fullName: "",
  dateOfBirth: "",
  city: "",
  homePhoneNumber: "",
  refereeName: "",
  refereeNumber: "",

  aadhaarNumber: "",
  aadhaarPhotos: {},
  panNumber: "",
  panPhotos: {},
  drivingLicenseNumber: "",
  drivingLicensePhotos: {},
  selfieUrl: null,

  documentOwnership: null,
  personalDocType: null,
  personalDocNumber: "",
  personalDocPhotos: {},

  lagganVideoUrl: null,
  lagganFcName: "",
  addressPlot: "",
  addressArea: "",
  addressPincode: "",
  addressState: "",
  addressCity: "",

  selectedSpoke: null,

  // Legacy — mirror structured fields
  panPhoto: null,
  selfieUri: null,
  currentAddress: "",
  refereePhone1: "",
  refereePhone2: "",
  lagganVideoUri: null,
  submittedByFCName: "",
  personalAadhaarNumber: "",
  personalAadhaarPhotos: {},
};

interface OnboardingContextValue {
  data: OnboardingData;
  updateData: (partial: Partial<OnboardingData>) => void;
  resetData: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue>({
  data: INITIAL_DATA,
  updateData: () => {},
  resetData: () => {},
});

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [data, setData] = useState<OnboardingData>(INITIAL_DATA);

  const updateData = useCallback((partial: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetData = useCallback(() => {
    setData(INITIAL_DATA);
  }, []);

  const value = useMemo(
    () => ({ data, updateData, resetData }),
    [data, updateData, resetData],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextValue =>
  useContext(OnboardingContext);

export default OnboardingContext;
